// src/routes/index.tsx
import { Navigate, createBrowserRouter } from "react-router-dom";
import Error404 from "../errorPages/Error404";
import WebsiteLayout from "../layouts/websitelayouts/WebsiteLayout";
import ErpLayout from "../layouts/erplayouts/Header";
import ClientLayout from "../layouts/clientlayouts/ClientLayout";

import HomePage from "../pages/website/HomePage";
import AboutUsPage from "../pages/website/AboutUsPage";
import ContactUsPage from "../pages/website/ContactUsPage";
import ServicesPage from "../pages/website/ServicesPage";
import ServiceDetailsPage from "../pages/website/ServiceDetailsPage";
import ResidentialProjectsPage from "../pages/website/projects/ResidentialProjectsPage";
import CommercialProjectsPage from "../pages/website/projects/CommercialProjectsPage";
import IndustrialProjectsPage from "../pages/website/projects/IndustrialProjectsPage";
import AllProjectsPage from "../pages/website/projects/AllProjectsPage";
import VideoReviewsPage from "../pages/website/VideoReviewsPage";
import ProjectsCompletedPage from "../pages/website/ProjectsCompletedPage";
import SafetyCompliancePage from "../pages/website/SafetyCompliancePage";
import { websitePaths, websiteRouteSegments } from "./websitePaths";

import AdminDashboard from "../pages/erp/AdminDashboard";
import RequestManager from "../pages/erp/RequestManager";
import ProjectListPage from "../pages/erp/ProjectListPage";
import ProjectCreatePage from "../pages/erp/ProjectCreatePage";
import ProjectImageManager from "../pages/erp/ProjectImageManager";
import ProjectProgressPage from "../pages/erp/ProjectProgressPage";
import AddEmployee from "../pages/erp/AddEmployee";
import EditEmployee from "../pages/erp/EditEmployee";
import ViewEmployee from "../pages/erp/ViewEmployee";
import EmployeeListPage from "../pages/erp/EmployeeListPage";
import ProjectProgressAnalyticsPage from "../pages/erp/ProjectProgressAnalyticsPage";
import ReviewManager from "../pages/erp/ReviewManager";
import AdminProfile from "../pages/erp/AdminProfile";
import ClientManager from "../pages/erp/ClientManager";
import ExpenseManager from "../pages/erp/ExpenseManager";
import PaymentManager from "../pages/erp/PaymentManager";
import CostEstimator from "../pages/erp/CostEstimator";
import ManagePasswordsPage from "../pages/erp/ManagePasswordsPage";
import NotificationComposer from "../pages/erp/NotificationComposer";
import ContactSettingsPage from "../pages/erp/ContactSettingsPage";
import PageImagesPage from "../pages/erp/PageImagesPage";
import ClientLoginPage from "../pages/client/ClientLoginPage";
import ClientDashboardPage from "../pages/client/ClientDashboardPage";
import ForgotPasswordPage from "../pages/shared/ForgotPasswordPage";
import ResetPasswordPage from "../pages/shared/ResetPasswordPage";
import LeaveReviewPage from "../pages/website/LeaveReviewPage";

const adminChildren = [
  { index: true, element: <AdminDashboard /> },
  { path: "requests", element: <RequestManager /> },
  { path: "projects", element: <ProjectListPage /> },
  { path: "projects/new", element: <ProjectCreatePage /> },
  { path: "projects/:projectId/edit", element: <ProjectCreatePage /> },
  { path: "projects/progress", element: <ProjectProgressPage /> },
  {
    path: "projects/progress-analytics",
    element: <ProjectProgressAnalyticsPage />,
  },
  { path: "project-images", element: <ProjectImageManager /> },
  { path: "employees",           element: <EmployeeListPage /> },
  { path: "employees/new",       element: <AddEmployee /> },
  { path: "employees/:id/view",  element: <ViewEmployee /> },
  { path: "employees/:id/edit",  element: <EditEmployee /> },
  { path: "reviews", element: <ReviewManager /> },
  { path: "profile", element: <AdminProfile /> },
  { path: "manage-passwords", element: <ManagePasswordsPage /> },
  { path: "clients", element: <ClientManager /> },
  { path: "expenses", element: <ExpenseManager /> },
  { path: "payments", element: <PaymentManager /> },
  { path: "cost-estimator", element: <CostEstimator /> },
  { path: "notifications", element: <NotificationComposer /> },
  { path: "contact-settings", element: <ContactSettingsPage /> },
  { path: "page-images",      element: <PageImagesPage /> },
];

export const router = createBrowserRouter([
  {
    path: "/",
    element: <WebsiteLayout />,
    errorElement: <Error404 />,
    children: [
      { index: true, element: <HomePage /> },
      { path: websiteRouteSegments.home, element: <HomePage /> },
      { path: websiteRouteSegments.about, element: <AboutUsPage /> },
      { path: websiteRouteSegments.contact, element: <ContactUsPage /> },
      { path: websiteRouteSegments.services, element: <ServicesPage /> },
      {
        path: `${websiteRouteSegments.services}/:slug`,
        element: <ServiceDetailsPage />,
      },
      {
        path: `${websiteRouteSegments.projects}/${websiteRouteSegments.residential}`,
        element: <ResidentialProjectsPage />,
      },
      {
        path: `${websiteRouteSegments.projects}/${websiteRouteSegments.commercial}`,
        element: <CommercialProjectsPage />,
      },
      {
        path: `${websiteRouteSegments.projects}/${websiteRouteSegments.industrial}`,
        element: <IndustrialProjectsPage />,
      },
      {
        path: `${websiteRouteSegments.projects}/${websiteRouteSegments.all}`,
        element: <AllProjectsPage />,
      },
      {
        path: `${websiteRouteSegments.projects}/${websiteRouteSegments.completed}`,
        element: <ProjectsCompletedPage />,
      },
      { path: websiteRouteSegments.safetyCompliance, element: <SafetyCompliancePage /> },
      {
        path: `${websiteRouteSegments.reviews}/${websiteRouteSegments.videos}`,
        element: <VideoReviewsPage />,
      },
      { path: "home", element: <Navigate to={websitePaths.home} replace /> },
      { path: "about", element: <Navigate to={websitePaths.about} replace /> },
      { path: "contact", element: <Navigate to={websitePaths.contact} replace /> },
      { path: "services", element: <Navigate to={websitePaths.services} replace /> },
      { path: "services/:slug", element: <Navigate to={websitePaths.services} replace /> },
      { path: "projects/residential", element: <Navigate to={websitePaths.projects.residential} replace /> },
      { path: "projects/commercial", element: <Navigate to={websitePaths.projects.commercial} replace /> },
      { path: "projects/industrial", element: <Navigate to={websitePaths.projects.industrial} replace /> },
      { path: "projects/all", element: <Navigate to={websitePaths.projects.all} replace /> },
      { path: "projects/completed", element: <Navigate to={websitePaths.projects.completed} replace /> },
      { path: "safety-compliance", element: <Navigate to={websitePaths.safetyCompliance} replace /> },
      { path: "reviews/videos", element: <Navigate to={websitePaths.reviews.videos} replace /> },
    ],
  },
  {
    path: "/admin",
    element: <ErpLayout />,
    errorElement: <Error404 />,
    children: adminChildren,
  },
  {
    path: "/erp",
    element: <ErpLayout />,
    errorElement: <Error404 />,
    children: adminChildren,
  },
  {
    path: "/client/login",
    element: <ClientLoginPage />,
    errorElement: <Error404 />,
  },
  {
    path: "/client",
    element: <ClientLayout />,
    errorElement: <Error404 />,
    children: [{ index: true, element: <ClientDashboardPage /> }],
  },
  { path: "/forgot-password", element: <ForgotPasswordPage />, errorElement: <Error404 /> },
  { path: "/reset-password",  element: <ResetPasswordPage />,  errorElement: <Error404 /> },
  { path: "/leave-review",    element: <LeaveReviewPage />,    errorElement: <Error404 /> },
  { path: "*", element: <Error404 /> },
]);
